import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ConstrainMode,
  DetailsListLayoutMode,
  DetailsRow,
  IDetailsHeaderProps,
  IRenderFunction,
  mergeStyleSets,
  ScrollablePane,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  Sticky,
  StickyPositionType,
  TooltipHost,
} from "@fluentui/react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { TableSpinner } from "../../components/TableSpinner";
import { useSelector } from "../../store/hooks";
import {
  selectShownLocations,
  setSortLocations,
} from "../../store/locations-page/locationsPage";
import {
  loadLocationsActions,
  setSelectedColumn,
} from "../../store/locations/locations";
import { AppDispatch } from "../../store/store";
import { ClientLocation } from "../../types/types";
import { defaultColumns, getColumnOptionsColumns } from "./columns/columns";
import { DeleteDialog } from "./DeleteDialog";
import { ExportToZipDialog } from "./ExportToZipDialog";
import { GenerateLocationReportDocumentDialog } from "./GenerateLocationReportDocumentDialog/GenerateLocationReportDocumentDialog";
import "./Locations.scss";
import { Navigation } from "./navigation/Navigation";
import { Summary } from "./Summary";

import { ChangeClientDialog } from "./ChangeClientDialog";

import { useConst } from "@uifabric/react-hooks";
import { useTheme } from "styled-components";
import { IStyledTheme } from "../../theme/types";
import { UsersReportDocumentDialog } from "./UsersReportDocumentDialog";
import {
  useColumnWidths,
  getDefaultWidthsFromColumns,
} from "../../hooks/useColumnWidths";
import {
  useTableSort,
} from "../../components/table";
import { ColumnOptions, useColumnOptionsState } from "../../components/column-options";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { ExportExcelGeneric } from "../../components/ExportExcelGeneric";
import moment from "moment";
import { SummaryPanelContext } from "./SummaryPanelContext";

export type PanelState = {
  isOpen: boolean;
  loc: ClientLocation | null;
};

const allLocationExcelColumns = [
  {
    key: "locationName",
    labelKey: "ram.locationList.locationName",
    width: 200,
  },
  { key: "clientName", labelKey: "ram.clientsList.clientName", width: 200 },
  {
    key: "webBasePartnerNumber",
    labelKey: "ram.clientsList.clientId",
    width: 150,
  },
  { key: "siccode", labelKey: "ram.locationList.sicCode", width: 150 },
  { key: "countryCode", labelKey: "ram.locationList.countryCode", width: 100 },
  { key: "postalCode", labelKey: "ram.locationList.postalCode", width: 100 },
  { key: "city", labelKey: "ram.locationList.city", width: 150 },
  {
    key: "turnoverInMioEur",
    labelKey: "ram.locationList.turnoverInMioEur",
    width: 120,
  },
  { key: "tsiinMioEur", labelKey: "ram.locationList.tsiinMioEur", width: 120 },
  { key: "pmlinMioEur", labelKey: "ram.locationList.pmlinMioEur", width: 120 },
  { key: "emlinMioEur", labelKey: "ram.locationList.emlinMioEur", width: 120 },
  {
    key: "ebitinMioEur",
    labelKey: "ram.locationList.ebitinMioEur",
    width: 120,
  },
  {
    key: "ygpcminMioEur",
    labelKey: "ram.locationList.ygpcminMioEur",
    width: 120,
  },
  {
    key: "deductiblesInMioEur",
    labelKey: "ram.locationList.deductiblesinMioEur",
    width: 120,
  },
  { key: "coords", labelKey: "ram.locationList.coordinates", width: 150 },
  {
    key: "insurersRaiting",
    labelKey: "ram.locationList.insurersRating",
    width: 120,
  },
];

export const Locations = () => {
  const { t } = useTranslation();
  const locations = useSelector(selectShownLocations);
  const sort = useSelector((s) => s.locationsPage.sort);
  const dispatch: AppDispatch = useDispatch();
  const areLocationsLoading = useSelector((s) => s.locations.isLoading);
  const deleteDialog = useSelector((s) => s.locationsPage.deleteLocationDialog);
  const changeClientDialog = useSelector(
    (s) => s.locationsPage.changeClientDialog,
  );
  const usersReportDocumentDialog = useSelector(
    (s) => s.locationsPage.usersReportDocumentDialog,
  );

  // Column options (replaces old column visibility, filtering, and reordering)
  const taxonomy = useSelector((s) => s.taxonomy);
  const columnOptionsTaxonomy = useMemo(() => {
    const result: { [key: string]: { code: string; id: number }[] } = {};
    for (const key of Object.keys(taxonomy)) {
      if (key === "response" || key === "isLoading" || key === "isLoadingUAD") continue;
      result[key] = (taxonomy as any)[key]?.items || [];
    }

    // Only show taxonomy values that exist in the current data
    const usedCountryIds = new Set<number>();
    const usedSicCodeIds = new Set<number>();
    for (const loc of locations || []) {
      if (!loc) continue;
      if (loc.countryCode != null) usedCountryIds.add(loc.countryCode);
      if (loc.siccode != null) usedSicCodeIds.add(loc.siccode);
    }
    if (result.Country) {
      result.Country = result.Country.filter((t) => usedCountryIds.has(t.id));
    }
    if (result.SicCode) {
      result.SicCode = result.SicCode.filter((t) => usedSicCodeIds.has(t.id));
    }

    return result;
  }, [taxonomy, locations]);

  const columnOptionsDefaultColumns = useMemo(
    () => getColumnOptionsColumns(t),
    [t],
  );

  const {
    isPanelOpen,
    openPanel,
    closePanel: closeColumnOptions,
    columns: draftColumns,
    views,
    selectedView,
    isReady,
    isViewLoading,
    onClickApply,
    onChangeView,
    setFilter: setColumnOptionsFilter,
    onSelectAll,
    onDeselectAll,
    onReorderColumn,
    onToggleColumnVisibility,
    onClickDeleteView,
    onClickClearAllFilters,
    onClickReset,
    onClickSaveView,
    onCreateView,
    onUpdateView,
    onResetAllStorage,
    activeColumns,
    visibleColumns,
    applyFiltersToData,
    activeFilterCount,
  } = useColumnOptionsState({
    tableId: "locations",
    defaultColumns: columnOptionsDefaultColumns,
    taxonomy: columnOptionsTaxonomy,
  });

  // Table sorting
  const { handleColumnClick, getSortIcon } = useTableSort({
    sort,
    setSort: (newSort) => dispatch(setSortLocations(newSort)),
    defaultSortKey: "locationCreatedAt",
    defaultSortDir: "desc",
  });

  // Apply filters to locations data
  const filteredLocations = useMemo(() => {
    return applyFiltersToData(locations || []);
  }, [locations, applyFiltersToData]);

  // const selectedColumn = useSelector((s) => s.locations.selectedColumn);

  const [panel, setPanel] = useState<PanelState>({
    isOpen: false,
    loc: null,
  });
  const exportToZipDialog = useSelector(
    (s) => s.locationsPage.exportToZipDialog,
  );
  const generateLocationReportDocumentDialog = useSelector(
    (s) => s.locationsPage.generateLocationReportDocumentDialog,
  );

  const theme = useTheme() as IStyledTheme;
  const classes = getClassNames(theme);
  const refExcelExport = useRef<ExcelExport>();

  const visibleLabelKeys = useMemo(
    () =>
      activeColumns
        .filter((c) => c.isVisible && c.key !== "assessmentPeriodStartedAt" && c.key !== "options")
        .map((c) => {
          const def = defaultColumns.find((d: any) => d.key === c.key);
          return def?.labelKey || "";
        })
        .filter(Boolean),
    [activeColumns],
  );

  const excelColumns = useMemo(
    () =>
      allLocationExcelColumns
        .filter((c) => visibleLabelKeys.includes(c.labelKey))
        .sort(
          (a, b) =>
            visibleLabelKeys.indexOf(a.labelKey) -
            visibleLabelKeys.indexOf(b.labelKey),
        ),
    [visibleLabelKeys],
  );

  const fileName = useMemo(() => {
    return "Locations" + moment(new Date()).format("_YYYY_MM_DD_HH_mm_ss");
  }, []);

  // Initialize column widths with localStorage persistence
  const defaultWidths = useMemo(
    () => getDefaultWidthsFromColumns(defaultColumns),
    [],
  );
  const { columnWidths, updateColumnWidth } = useColumnWidths({
    storageKey: "locations",
    defaultWidths,
  });

  const selection = useConst(
    () =>
      new Selection({
        onSelectionChanged: async () => {
          const selected = selection.getSelection() as any;
          dispatch(setSelectedColumn(selected[0]));
        },
      }),
  );

  useEffect(() => {
    dispatch(loadLocationsActions.trigger());
  }, []);

  const detailsListColumns = useMemo(() => {
    const defaultColumnsMap = new Map(
      defaultColumns.map((c: any) => [c.key, c]),
    );

    // assessmentPeriodStartedAt (status dot) always comes first;
    // all other columns follow activeColumns order and respect visibility
    const orderedKeys = [
      "assessmentPeriodStartedAt",
      ...activeColumns
        .filter((c) => c.key !== "assessmentPeriodStartedAt" && c.isVisible)
        .map((c) => c.key),
    ];

    const mapColumn = (column: any) => {
      let iconName = column.iconName;

      if (column.key === sort.key && column.isSortable) {
        iconName = getSortIcon(column.key);
      }

      const isResizable = column.isResizable === true;
      if (isResizable) {
        const minWidth = column.minWidth || 50;
        const currentWidth = Math.max(
          columnWidths[column.key] || minWidth,
          minWidth,
        );
        return {
          ...column,
          name: column.name ? t(column.name) : "",
          iconName,
          width: currentWidth,
          minWidth,
          maxWidth: 1000,
          isResizable: true,
          calculatedWidth: currentWidth,
        };
      }

      return {
        ...column,
        name: column.name ? t(column.name) : "",
        iconName,
      };
    };

    const result = orderedKeys
      .map((key) => defaultColumnsMap.get(key))
      .filter(Boolean)
      .map(mapColumn)
      .concat([{ key: "filler", label: "", width: 0 }]);

    return result;
  }, [sort, t, columnWidths, getSortIcon, activeColumns]);

  const onClickHeaderCell = (column) => {
    if (!column.isSortable) return;
    handleColumnClick(column.key, column.isSortable);
  };

  const onRenderDetailsHeader = (
    props: IDetailsHeaderProps | undefined,
    defaultRender: IRenderFunction<IDetailsHeaderProps> | undefined,
  ) => (
    <Sticky
      stickyPosition={StickyPositionType.Header}
      isScrollSynced
      stickyClassName={classes.table}
    >
      {defaultRender!({
        ...props,
        styles: { root: { paddingTop: 0 } },
      })}
    </Sticky>
  );

  const handleSetSummaryPanel = useCallback((value) => {
    setPanel(value);
  }, []);

  const handleShowSummary = useCallback((loc: ClientLocation) => {
    setPanel({ isOpen: true, loc });
  }, []);

  const onRenderRow = (props) => {
    return <DetailsRow {...props} />;
  };

  return (
    <SummaryPanelContext.Provider value={handleShowSummary}>
      <Navigation
        refExcelExport={refExcelExport?.current}
        filteredLocations={filteredLocations}
        visibleLabelKeys={visibleLabelKeys}
        onOpenColumnOptions={openPanel}
        activeFilterCount={activeFilterCount}
        filteredCount={filteredLocations.length}
      />
      {isPanelOpen && (
        <ColumnOptions
          closePanel={closeColumnOptions}
          columns={draftColumns}
          onClickApply={onClickApply}
          views={views}
          selectedView={selectedView}
          onChangeView={onChangeView}
          isReady={isReady}
          isViewLoading={isViewLoading}
          setFilter={setColumnOptionsFilter}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
          taxonomy={columnOptionsTaxonomy}
          onClickDeleteView={onClickDeleteView}
          onClickClearAllFilters={onClickClearAllFilters}
          onClickReset={onClickReset}
          onClickSaveView={onClickSaveView}
          onCreateView={onCreateView}
          onUpdateView={onUpdateView}
          onReorderColumn={onReorderColumn}
          onToggleColumnVisibility={onToggleColumnVisibility}
          onResetAllStorage={onResetAllStorage}
          t={t}
        />
      )}
      <div className={classes.container} id="scrollableDiv">
        <ScrollablePane
          styles={{
            stickyAbove: {
              zIndex: 3,
            },
          }}
        >
          <ShimmeredDetailsList
            items={filteredLocations || []}
            compact={false}
            selection={selection}
            columns={detailsListColumns || []}
            selectionMode={SelectionMode.single}
            setKey="single"
            isSelectedOnFocus
            layoutMode={DetailsListLayoutMode.fixedColumns}
            isHeaderVisible={true}
            // onRenderItemColumn={(item, index, column) => {
            //   if (column.key === "options") {
            //     return (
            //       <FixedCell no={index} row={item}>
            //         <CenteredCell>
            //           <ContextButton loc={item} />
            //         </CenteredCell>
            //       </FixedCell>
            //     );
            //   }
            //   return column[index];
            // }}
            enableShimmer={areLocationsLoading && !locations}
            shimmerLines={20}
            selectionPreservedOnEmptyClick={true}
            onItemInvoked={(row) => {
              handleSetSummaryPanel({ isOpen: true, loc: row });
            }}
            enterModalSelectionOnTouch={true}
            // onItemContextMenu={onItemContextMenu}
            ariaLabelForSelectionColumn="Toggle selection"
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            checkButtonAriaLabel="Row checkbox"
            constrainMode={ConstrainMode.unconstrained}
            onColumnHeaderClick={(ev, column) => {
              onClickHeaderCell(column);
            }}
            onColumnResize={(column, newWidth) => {
              if (column?.key && column.key !== "filler") {
                updateColumnWidth(column.key, newWidth ?? 100);
              }
            }}
            onRenderRow={onRenderRow}
            className={classes.table}
            onRenderDetailsHeader={(
              props: IDetailsHeaderProps | undefined,
              defaultRender: IRenderFunction<IDetailsHeaderProps> | undefined,
            ) => onRenderDetailsHeader(props, defaultRender)}
          />
        </ScrollablePane>
      </div>
      {deleteDialog.isOpen && <DeleteDialog />}
      {changeClientDialog.isOpen && <ChangeClientDialog />}
      {areLocationsLoading && <TableSpinner />}
      {exportToZipDialog.isOpen && <ExportToZipDialog />}
      {generateLocationReportDocumentDialog.isOpen && (
        <GenerateLocationReportDocumentDialog />
      )}
      {usersReportDocumentDialog.isOpen && <UsersReportDocumentDialog />}

      {panel.isOpen && (
        <Summary panel={panel} setPanel={handleSetSummaryPanel} />
      )}
      <ExportExcelGeneric
        ref={refExcelExport}
        columns={excelColumns}
        fileName={fileName}
      />
    </SummaryPanelContext.Provider>
  );
};

const getClassNames = (theme) =>
  mergeStyleSets({
    container: {
      // height: "90vh",
      height: "calc(100vh  - 100px)",

      marginBottom: "20px",
      position: "relative",
      overflow: "auto",
      width: "100%",
    },
    listContainer: {
      display: "flex",
    },

    table: {
      ".ms-List-cell:nth-child(odd) .ms-DetailsRow": {
        background: theme.isDark
          ? "rgb(2, 7, 10)"
          : theme.palette?.themeLighterAlt,
      },
      ".ms-DetailsRow ": {
        height: "30px !important",
      },

      ".ms-DetailsHeader-cellTitle": {
        justifyContent: "flex-start",
      },
      "[data-item-key='fleetName'] span": {
        justifyContent: "flex-start",
      },

      ".ms-DetailsRow-cell": {
        display: "flex",
        justifyContent: "flex-start",
      },
      ".ms-DetailsRow-cell:first-child": {
        display: "flex",
        justifyContent: "flex-start",
      },

      ".ms-DetailsHeader-cellName i": {
        marginRight: "5px",
        fontSize: "smaller",
        fontWeight: "400",
      },

      //******************************************************************************************************* */
      // Sticky columns — use data-item-key selectors so they work regardless of column order

      // Header: checkbox cell
      ".ms-DetailsHeader-cell:nth-child(1)": {
        position: "sticky",
        inset: 0,
        zIndex: 99999,
        background: theme.palette.white,
      },

      // Header: status dot
      "[data-item-key='assessmentPeriodStartedAt'].ms-DetailsHeader-cell": {
        position: "sticky",
        inset: 48,
        zIndex: 99999,
        background: theme.palette.white,
      },
      // Header: location name
      "[data-item-key='locationName'].ms-DetailsHeader-cell": {
        position: "sticky",
        inset: 108,
        zIndex: 99999,
        background: theme.palette.white,
      },
      // Header: options kebab
      "[data-item-key='options'].ms-DetailsHeader-cell": {
        position: "sticky",
        inset: 370,
        zIndex: 99999,
        background: theme.palette.white,
      },

      "[data-item-key='assessmentPeriodStartedAt'].ms-DetailsHeader-cell:hover, [data-item-key='locationName'].ms-DetailsHeader-cell:hover, [data-item-key='options'].ms-DetailsHeader-cell:hover":
        {
          backgroundColor: theme.palette.neutralLighter,
        },

      /// Row cells — use data-automation-key which Fluent UI sets on row cells

      // Row: status dot
      ".ms-DetailsRow-fields [data-automation-key='assessmentPeriodStartedAt']": {
        position: "sticky",
        left: 48,
        zIndex: 99999,
        alignItems: "center",
      },
      // Row: location name
      ".ms-DetailsRow-fields [data-automation-key='locationName']": {
        position: "sticky",
        left: 108,
        zIndex: 99999,
      },
      // Row: options kebab
      ".ms-DetailsRow-fields [data-automation-key='options']": {
        position: "sticky",
        left: 370,
        zIndex: 99999,
      },

      // checkbox in row
      ".ms-DetailsRow-cellCheck:nth-child(1)": {
        position: "sticky",
        left: 0,
        zIndex: 1,
      },

      // ********************************************************* //
      // Alternating row backgrounds for sticky cells
      ".ms-List-cell:nth-child(odd) .ms-DetailsRow-cellCheck:nth-child(1)": {
        background: theme.palette?.themeLighterAlt,
      },
      ".ms-List-cell:nth-child(odd) .ms-DetailsRow-fields [data-automation-key='assessmentPeriodStartedAt']": {
        background: theme.palette?.themeLighterAlt,
      },
      ".ms-List-cell:nth-child(odd) .ms-DetailsRow-fields [data-automation-key='locationName']": {
        background: theme.palette?.themeLighterAlt,
      },
      ".ms-List-cell:nth-child(odd) .ms-DetailsRow-fields [data-automation-key='options']": {
        background: theme.palette?.themeLighterAlt,
      },

      ".ms-List-cell:nth-child(even) .ms-DetailsRow-cellCheck:nth-child(1)": {
        background: theme.palette?.white,
      },
      ".ms-List-cell:nth-child(even) .ms-DetailsRow-fields [data-automation-key='assessmentPeriodStartedAt']": {
        background: theme.palette?.white,
      },
      ".ms-List-cell:nth-child(even) .ms-DetailsRow-fields [data-automation-key='locationName']": {
        background: theme.palette?.white,
      },
      ".ms-List-cell:nth-child(even) .ms-DetailsRow-fields [data-automation-key='options']": {
        background: theme.palette?.white,
      },

      ///
      ".ms-List-cell .ms-DetailsRow:hover": {
        backgroundColor: theme.isDark
          ? "rgb(9, 27, 41) !important"
          : "rgb(208, 231, 248) !important",
      },
      ".ms-List-cell:hover, .ms-List-cell .is-selected": {
        backgroundColor: theme.isDark
          ? "rgb(9, 27, 41) !important"
          : "rgb(208, 231, 248) !important",
      },

      //
      ".ms-List-cell:hover .ms-DetailsRow-cell > * ": {
        height: "41px",
        padding: "0 !important",
        backgroundColor: theme.isDark
          ? "rgb(9, 27, 41) !important"
          : "rgb(208, 231, 248) !important",
        position: "relative",
      },
      //

      ".ms-List-cell:hover, .ms-List-cell .is-selected .ms-DetailsRow-cell > *":
        {
          height: "41px",
          padding: "0 !important",
          backgroundColor: theme.isDark
            ? "rgb(9, 27, 41) !important"
            : "rgb(208, 231, 248) !important",
          position: "relative",
        },

      //
      ".ms-List-cell:hover .ms-DetailsRow-cell > *  .ms-DetailsRow-cell[class^='cellUnpadded'], , div[class*=' cellUnpadded']":
        {
          paddingRight: "0 !important",
          paddingLeft: "0 !important",
        },

      ".ms-List-cell:hover, .ms-List-cell .is-selected .ms-DetailsRow-cell > *  .ms-DetailsRow-cell[class^='cellUnpadded'], , div[class*=' cellUnpadded']":
        {
          paddingRight: "0 !important",
          paddingLeft: "0 !important",
        },
    },
  });

